import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import SPMapSection from "~/features/salesPartner/components/MapSection/SPMapSection";
import SearchCard, { type Option } from "~/features/salesPartner/components/SearchCard/SalesPartnerSearchBox";
import SalesPartnersTilesList from "~/features/salesPartner/components/SalesPartnersTilesList/SalesPartnersTilesList";
import {
  type CenterValue,
  type GoogleMarker,
  type PaginationDataType,
} from "~/domains/salesPartner/types/SalesPartnerTypes";
import { redirectToUrl } from "~/utils/url/urlUtils";
import {
  SearchType,
  type SalesPartnerSearchQuery,
  type UrlParams,
} from "~/domains/salesPartner/api/getSalesPartnerSearch";
import { getBasicSalesPartnerData } from "~/domains/salesPartner/api/getBasicSalesPartnerData";
import { selectWishlistThunk } from "~/features/wishlist/redux/wishlistSlice";
import { useDispatch, useSelector } from "react-redux";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { GoogleApiProvider, useIsGoogleApiLoaded } from "~/features/salesPartner/providers/GoogleApiProvider";
import { Col, Row } from "~/common/components/Layout";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import Modal, { ModalSize } from "~/common/components/Modal";
import { SPCompanyDetails } from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetails";
import GmapsCardRightUnregistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightUnregistered";
import GmapsCardRightRegistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightRegistered";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { Alert, Box, Button, Stack, styled } from "~/common/mui";
import { useShowAffiliateMainBanner } from "~/domains/affiliate/utils/utils";
import { RegisterToSaveSnackbar } from "~/features/salesPartner/components/SalesPartnerSnackbars";
import { trackSalesPartnerFinderSelectDefault } from "~/app/analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import classNames from "classnames";
import { SPCompanyDetailsCard } from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCard";
import { CART_PAGE, CART_STEP_2, REGISTER_PAGE } from "~/utils/url/staticUrls";
import { B2CCartProcessTile } from "~/features/cart/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import { buildSalesPartnerSearchTrackingValues, trackPartnerFinderSearch } from "~/app/analytics/ga/salesPartner";
import { transformSelectedMarkerData } from "~/features/cart/B2CRequestPage/utils";
import { selectSavedAffiliate } from "~/domains/affiliate/redux/selectors";
import { clearSavedSubsidiaryThunk, saveAffiliateThunk } from "~/domains/affiliate/redux/actions";
import { usePreferredSalesPartner } from "~/features/salesPartner/hooks/usePreferredSalesPartner";
import { type SalesPartnerSearchError, useSalesPartnerSearch } from "~/features/salesPartner/hooks/useSalesPartnerSearch";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useGuestCheckoutDialog } from "~/features/cart/B2CCart/components/GuestCheckoutDialog/useGuestCheckoutDialog";
import { NuqsAdapter } from "nuqs/adapters/react";
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";

import { Tab } from "~/common/mui/Tabs/Tab";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { salesPartnerMobileDetailsModalSx } from "~/features/salesPartner/components/salesPartnerModalStyles";

interface ConfigurationData {
  shareLinearConfigurationData: string[];
  productCompetences: Option[];
  certifiedCompetences: Option[];
  selectedProductCompetences: string[];
}

interface Props {
  data: ConfigurationData | undefined;
}

export const INITIAL_RADIUS = 100;

const RESULTS_ID = "results";

const QUERY_PARAMS = {
  query: parseAsString.withDefault(""),
  radius: parseAsInteger.withDefault(INITIAL_RADIUS),
  searchType: parseAsStringEnum(Object.values(SearchType)).withDefault(SearchType.Address),
  productCompetence: parseAsArrayOf(parseAsString).withDefault([]),
  certifiedCompetence: parseAsArrayOf(parseAsString).withDefault([]),
  countryCode: parseAsString.withDefault(""),
  lat: parseAsFloat,
  lng: parseAsFloat,
  preferredOnly: parseAsBoolean.withDefault(false),
};

const QUERY_OPTIONS = { history: "replace" as const };

const hasExecutableQuery = (query: SalesPartnerSearchQuery) =>
  !!query.query ||
  query.productCompetence.length > 0 ||
  query.certifiedCompetence.length > 0 ||
  query.radius !== INITIAL_RADIUS ||
  !!query.countryCode ||
  query.lat !== null ||
  query.lng !== null;

const normalizeQuery = (query: SalesPartnerSearchQuery, hasPreferredSalesPartner: boolean): SalesPartnerSearchQuery => {
  const hasCoordinates = query.lat !== null && query.lng !== null;
  const isNonAddressSearch = query.searchType === SearchType.Partner || query.searchType === SearchType.CertificateID;
  const normalized: SalesPartnerSearchQuery = {
    ...query,
    productCompetence: query.productCompetence ?? [],
    certifiedCompetence: query.certifiedCompetence ?? [],
    countryCode: isNonAddressSearch ? "" : query.countryCode.toLowerCase(),
    lat: isNonAddressSearch ? null : hasCoordinates ? query.lat : null,
    lng: isNonAddressSearch ? null : hasCoordinates ? query.lng : null,
    preferredOnly: hasPreferredSalesPartner ? query.preferredOnly : false,
  };

  return normalized;
};

const buildSearchParams = (query: SalesPartnerSearchQuery, preferredAffiliateId?: string): UrlParams => ({
  query: query.query,
  radius: query.radius,
  searchType: query.searchType,
  productCompetence: query.productCompetence,
  certifiedCompetence: query.certifiedCompetence,
  countryCode: query.countryCode,
  lat: query.lat ?? undefined,
  lng: query.lng ?? undefined,
  salesPartnerId: query.preferredOnly ? preferredAffiliateId ?? "" : "",
});

const DEFAULT_QUERY: SalesPartnerSearchQuery = {
  query: "",
  radius: INITIAL_RADIUS,
  searchType: SearchType.Address,
  productCompetence: [],
  certifiedCompetence: [],
  countryCode: "",
  lat: null,
  lng: null,
  preferredOnly: false,
};

const DEFAULT_PAGINATION: PaginationDataType = {
  currentPage: 0,
  numberOfPages: 1,
  pageSize: 7,
  totalNumberOfResults: 0,
};

export interface SalesPartnerPageViewProps {
  data?: ConfigurationData;
  query?: SalesPartnerSearchQuery;
  markers?: GoogleMarker[];
  selectedMarker?: GoogleMarker;
  paginationData?: PaginationDataType;
  markersLoading?: boolean;
  searchError?: SalesPartnerSearchError | null;
  hasPreferredSalesPartner?: boolean;
  isCheckout?: boolean;
  map?: ReactNode;
}

export const SalesPartnerPageView = ({
  data,
  query = DEFAULT_QUERY,
  markers = [],
  selectedMarker,
  paginationData,
  markersLoading = false,
  searchError = null,
  hasPreferredSalesPartner = false,
  isCheckout = false,
  map,
}: SalesPartnerPageViewProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const [activeTab, setActiveTab] = useState("results");
  const effectivePagination = paginationData ?? {
    ...DEFAULT_PAGINATION,
    pageSize: markers.length || DEFAULT_PAGINATION.pageSize,
    totalNumberOfResults: markers.length,
  };

  const visibleMarkers = markers.slice(
    effectivePagination.currentPage * effectivePagination.pageSize,
    (effectivePagination.currentPage + 1) * effectivePagination.pageSize,
  );

  const results = (
    <SalesPartnersTilesList
      markers={visibleMarkers}
      selectedMarker={selectedMarker}
      onTileClick={() => {}}
      paginationData={effectivePagination}
      markersLoading={markersLoading}
      updatePaginationCurrentPage={() => {}}
      showMoreMarkers={() => {}}
      onToggleDefault={() => {}}
      isCheckout={isCheckout}
    />
  );

  const mapContent = map ?? <SalesPartnerMapPlaceholder>{t("page.salespartner.view.map")}</SalesPartnerMapPlaceholder>;

  return (
    <SalesPartnerStylesRoot>
      <div className="scha__sales-partner">
        <h3 className="headline-medium mb-3">{t("partnerFinder.title")}</h3>
        <SearchCard
          query={query}
          markersLoading={markersLoading}
          onCommit={() => {}}
          hasPreferredSalesPartner={hasPreferredSalesPartner}
          productCompetencesOptions={data?.productCompetences}
          certifiedCompetencesOptions={data?.certifiedCompetences}
          isCheckout={isCheckout || !!data?.shareLinearConfigurationData?.length}
        />
      </div>

      {searchError && (
        <Alert severity="error" className="mb-3">
          {t("partnerFinder.error.searchFailed", { defaultValue: searchError.message })}
        </Alert>
      )}

      <div id={RESULTS_ID}>
        {isDesktop && <DesktopLayout results={results} map={mapContent} />}
        {!isDesktop && (
          <MobileLayout results={results} map={mapContent} activeTab={activeTab} setActiveTab={setActiveTab} />
        )}
      </div>
    </SalesPartnerStylesRoot>
  );
};

const OFFSET_CONSTANT = 122.88;
const calculateOffset = (zoom: number) => (zoom > 0 ? OFFSET_CONSTANT / 2 ** zoom : OFFSET_CONSTANT);

const SalesPartnerPage = (props: Props) => {
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const {
    data: { shareLinearConfigurationData, productCompetences, certifiedCompetences, selectedProductCompetences } = {},
  } = props;

  const isCheckout = window.location.pathname.endsWith(CART_STEP_2);

  const PAGE_SIZE = isDesktop || !isCheckout ? 7 : 20;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [query, setQuery] = useQueryStates(QUERY_PARAMS, QUERY_OPTIONS);
  const [zoomValue, setZoomValue] = useState<number>(5);
  const [center, setCenter] = useState<CenterValue>({
    lat: 52.520008,
    lng: 13.404954,
  });
  const [selectedMarker, setSelectedMarker] = useState<GoogleMarker>();
  const [selectedMarkerMap, setSelectedMarkerMap] = useState<GoogleMarker>();

  const preferredSalesPartner = usePreferredSalesPartner();
  const savedAffiliate = useSelector(selectSavedAffiliate);
  const { isLoaded: googleApiLoaded } = useIsGoogleApiLoaded();

  const hasLinearData = !!shareLinearConfigurationData && shareLinearConfigurationData.length > 0;

  const handleMarkersLoaded = useCallback((markers: GoogleMarker[], selectFirst: boolean) => {
    if (selectFirst) {
      updateSelectedMarker(markers[0]);
    }
  }, []);

  const {
    isLoading: markersLoading,
    allMarkers,
    paginationData,
    error: searchError,
    search: searchSalesPartners,
    searchOnMount,
    setPaginationData,
  } = useSalesPartnerSearch({
    pageSize: PAGE_SIZE,
    preferredSalesPartnerId: preferredSalesPartner?.affiliateId,
    onMarkersLoaded: handleMarkersLoaded,
  });

  const searchInitialized = useRef(false);
  const skipPreferredReconcileOnce = useRef(false);

  useEffect(() => {
    if (!googleApiLoaded || searchInitialized.current) {
      return;
    }

    searchInitialized.current = true;
    const normalizedQuery = normalizeQuery(query, !!preferredSalesPartner);
    if (hasExecutableQuery(normalizedQuery)) {
      skipPreferredReconcileOnce.current = true;
      setQuery(normalizedQuery);
      searchOnMount(buildSearchParams(normalizedQuery, preferredSalesPartner?.affiliateId));
      return;
    }

    skipPreferredReconcileOnce.current = true;
    getBasicSalesPartnerData().then((res) => {
      const countryCode = res.countryCode.toLowerCase();
      const defaultQuery: SalesPartnerSearchQuery = {
        query: t(`country.${countryCode}`),
        radius: -1,
        searchType: SearchType.Address,
        productCompetence: isCheckout || hasLinearData ? selectedProductCompetences ?? [] : [],
        certifiedCompetence: [],
        countryCode,
        lat: null,
        lng: null,
        preferredOnly: query.preferredOnly || !!preferredSalesPartner,
      };
      const normalizedDefaultQuery = normalizeQuery(defaultQuery, !!preferredSalesPartner);
      setQuery(normalizedDefaultQuery);
      searchOnMount(buildSearchParams(normalizedDefaultQuery, preferredSalesPartner?.affiliateId));
    });
  }, [googleApiLoaded]);

  const committedQuery = normalizeQuery(query, !!preferredSalesPartner);
  const searchValues = buildSalesPartnerSearchTrackingValues(committedQuery);

  const runSearch = useCallback(
    (nextQuery: SalesPartnerSearchQuery) => {
      const normalizedQuery = normalizeQuery(nextQuery, !!preferredSalesPartner);
      trackPartnerFinderSearch(buildSalesPartnerSearchTrackingValues(normalizedQuery), "Finding Performed", "Click");
      searchSalesPartners(buildSearchParams(normalizedQuery, preferredSalesPartner?.affiliateId), 0);
    },
    [preferredSalesPartner, searchSalesPartners],
  );

  const commitSearch = useCallback(
    (nextQuery: SalesPartnerSearchQuery) => {
      const normalizedQuery = normalizeQuery(nextQuery, !!preferredSalesPartner);
      setQuery(normalizedQuery);
      runSearch(normalizedQuery);
    },
    [preferredSalesPartner, runSearch, setQuery],
  );

  const previousPreferredAffiliateId = useRef(preferredSalesPartner?.affiliateId);

  useEffect(() => {
    if (skipPreferredReconcileOnce.current) {
      skipPreferredReconcileOnce.current = false;
      return;
    }

    const currentPreferredAffiliateId = preferredSalesPartner?.affiliateId;
    const previousPreferredAffiliateIdValue = previousPreferredAffiliateId.current;
    previousPreferredAffiliateId.current = currentPreferredAffiliateId;

    if (!searchInitialized.current || !query.preferredOnly) {
      return;
    }

    if (!currentPreferredAffiliateId) {
      commitSearch({ ...committedQuery, preferredOnly: false });
      return;
    }

    if (previousPreferredAffiliateIdValue && previousPreferredAffiliateIdValue !== currentPreferredAffiliateId) {
      commitSearch(committedQuery);
    }
  }, [commitSearch, committedQuery, preferredSalesPartner?.affiliateId, query.preferredOnly]);

  const trackFinderInteraction = useCallback(
    (action: string, rawAction: string, selectedPartner?: Parameters<typeof trackPartnerFinderSearch>[3]) => {
      trackPartnerFinderSearch(searchValues, action, rawAction, selectedPartner);
    },
    [searchValues],
  );

  function sortByTranslatedLabel(products: Option[] | undefined) {
    if (!products) {
      return undefined;
    }

    const getEnglishLabel = (label: string) => {
      return t(label, { lng: "en" });
    };

    const productsWithTranslated = products
      .slice()
      .sort((a, b) => getEnglishLabel(a.label).localeCompare(getEnglishLabel(b.label)));

    return productsWithTranslated.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }

  const updateSelectedMarker = useCallback((marker: GoogleMarker | undefined) => {
    setSelectedMarker(marker);
    setSelectedMarkerMap(undefined);
    window.setTimeout(() => setSelectedMarkerMap(marker), 50);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("wl-id")) {
      dispatch(selectWishlistThunk(String(urlParams.get("wl-id"))));
    }
  }, []);

  const { val: isDetailsPopupOpen, setTrue: openDetailsPopup, setFalse: closeDetailsPopup } = useBooleanState(false);

  const closeDetailsCard = () => {
    closeDetailsPopup();
    setSelectedMarker(undefined);
    setSelectedMarkerMap(undefined);
  };

  const selectSalesPartner = (marker: GoogleMarker | undefined, canOpenPopup = true) => {
    if (marker && selectedMarker && marker === selectedMarker) {
      closeDetailsCard();
      return;
    }

    updateSelectedMarker(marker);

    const pageIndex = marker ? Math.floor(allMarkers.indexOf(marker) / PAGE_SIZE) : 0;
    if (pageIndex >= 0) {
      setPaginationData((oldData) => ({ ...oldData, currentPage: pageIndex }));
    }

    if (marker) {
      const lat = marker.lat + (isDesktop ? calculateOffset(zoomValue) : 0);
      setCenter({ lat: lat, lng: marker.lng });
      window.location.href = "#" + RESULTS_ID;

      if (!isDesktop && canOpenPopup) {
        openDetailsPopup();
      }
    }
    trackFinderInteraction("Sales Partner Selected", "Click", transformSelectedMarkerData(t, marker));
  };

  const updatePaginationCurrentPage = (newPageNumber: number) => {
    setPaginationData({
      ...paginationData,
      currentPage: newPageNumber,
    });
  };

  const showMoreMarkers = () => {
    setPaginationData({
      ...paginationData,
      pageSize: paginationData.pageSize + PAGE_SIZE,
    });
  };

  const listMarkers = allMarkers.slice(
    paginationData.currentPage * paginationData.pageSize,
    (paginationData.currentPage + 1) * paginationData.pageSize,
  );

  const {
    val: showRegisterSnackbar,
    setTrue: openRegisterSnackbar,
    setFalse: closeRegisterSnackbar,
  } = useBooleanState(false);

  const isInitialised = useRef(false);

  useEffect(() => {
    if (!isInitialised.current) {
      return;
    }
    if (savedAffiliate && !userIsLoggedIn()) {
      openRegisterSnackbar();
    }
  }, [savedAffiliate]);

  useEffect(() => {
    isInitialised.current = true;
  }, []);

  useEffect(() => {
    if (preferredSalesPartner) {
      const preferredMarker = allMarkers.find((marker) => marker.affiliateId === preferredSalesPartner.affiliateId);
      updateSelectedMarker(preferredMarker);
    }
  }, [allMarkers]);

  const handleConfirmClick = () => {
    closeDetailsPopup();
    if (selectedMarker?.subsidiaryName) {
      handleB2CCheckout(selectedMarker.subsidiaryId);
    }
  };

  const setDefaultLocation = (marker: GoogleMarker, isDefault: boolean) => {
    if (isDefault) {
      const address = [marker.address.line1, marker.address.line2, marker.address.town, marker.address.country.name]
        .filter(Boolean)
        .join(" ");
      trackSalesPartnerFinderSelectDefault(marker.address.companyNameDisplay, marker.address.postalCode, address);
      dispatch(
        saveAffiliateThunk({
          name: marker.address.companyNameDisplay,
          affiliateId: marker.affiliateId,
          logo: "",
          subsidiaries: [],
          salesPartnerId: marker.subsidiaryId,
        }),
      );
    } else {
      dispatch(clearSavedSubsidiaryThunk());
    }
  };

  const loggedIn = userIsLoggedIn();
  const registeredUser = userIsRegistered();
  const [activeTab, setActiveTab] = useState("results");

  const results = (
    <SalesPartnersTilesList
      markers={listMarkers}
      selectedMarker={selectedMarker}
      onTileClick={selectSalesPartner}
      paginationData={paginationData}
      markersLoading={markersLoading}
      updatePaginationCurrentPage={updatePaginationCurrentPage}
      showMoreMarkers={showMoreMarkers}
      onToggleDefault={setDefaultLocation}
      isCheckout={isCheckout}
    />
  );

  const map = (
    <SPMapSection
      onClose={closeDetailsCard}
      markers={allMarkers}
      selectedMarker={selectedMarkerMap}
      center={center}
      zoomValue={zoomValue}
      setZoomValue={setZoomValue}
      selectSalesPartner={selectSalesPartner}
      shareLinearConfigurationData={shareLinearConfigurationData}
      openDetailsPopup={openDetailsPopup}
      openRegisterSnackbar={openRegisterSnackbar}
      isCheckout={isCheckout}
      activeTab={activeTab}
      searchValues={searchValues}
    />
  );

  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
  }, [activeTab]);

  const isAffiliate = useShowAffiliateMainBanner();

  const showMapCb = () => {
    setActiveTab("map");
    window.setTimeout(() => redirectToUrl("#" + RESULTS_ID), 0);
    closeDetailsPopup();
  };

  const [handleB2CCheckout, guestCheckoutDialog] = useGuestCheckoutDialog();

  return (
    <SalesPartnerStylesRoot>
      {guestCheckoutDialog}
      {isCheckout && !isAffiliate && <B2CCartProcessTile activeIndex={1} className="mb-4" />}
      <RegisterToSaveSnackbar
        open={showRegisterSnackbar}
        onClose={closeRegisterSnackbar}
        selectedMarker={selectedMarkerMap}
        searchValues={searchValues}
      />
      <div className="scha__sales-partner">
        <h3 className="headline-medium mb-3">{t("partnerFinder.title")}</h3>
        <SearchCard
          query={committedQuery}
          markersLoading={markersLoading}
          onCommit={commitSearch}
          hasPreferredSalesPartner={!!preferredSalesPartner}
          productCompetencesOptions={sortByTranslatedLabel(productCompetences)}
          certifiedCompetencesOptions={sortByTranslatedLabel(certifiedCompetences)}
          isCheckout={isCheckout || hasLinearData}
        />
      </div>

      {searchError && (
        <Alert severity="error" className="mb-3">
          {t("partnerFinder.error.searchFailed", { defaultValue: searchError.message })}
        </Alert>
      )}

      <div id={RESULTS_ID}>
        {isDesktop && <DesktopLayout results={results} map={map} />}

        {!isDesktop && <MobileLayout results={results} map={map} activeTab={activeTab} setActiveTab={setActiveTab} />}
      </div>

      {isDetailsPopupOpen && selectedMarker && (
        <Modal
          size={ModalSize.SMALL}
          onClose={closeDetailsCard}
          modalClassName={classnames(!isTablet && "scha__sp__mobile-details")}
          modalPaperSx={salesPartnerMobileDetailsModalSx}
          footerChildren={
            isTablet ? (
              <div className="d-flex flex-justify-between w-100">
                <Button onClick={closeDetailsCard} variant="text">
                  {t("bulkUpload.buttons.cancel")}
                </Button>
                <SPButtonsRow className={classNames("d-flex", userIsRegistered() && "scha__sp__reverse")}>
                  {isCheckout ? (
                    <Button onClick={handleConfirmClick}>{t("partnerFinder.search.confirm")}</Button>
                  ) : (
                    <Button
                      startIcon={<ShoppingCartIcon />}
                      color={userIsRegistered() ? "primary" : "secondary"}
                      href={CART_PAGE}
                    >
                      {t("addToCartPopup.checkoutBtn")}
                    </Button>
                  )}
                  {!userIsRegistered() && (
                    <Button href={REGISTER_PAGE} startIcon={<PersonOutlineOutlinedIcon />}>
                      {t("partnerFinder.register.button")}
                    </Button>
                    )}
                </SPButtonsRow>
              </div>
            ) : (
              <SpModalFilterFooter>
                <Stack gap={1} direction="row" className="d-flex flex-justify-between w-100">
                  {userIsRegistered() ? (
                    <>
                      <Button onClick={closeDetailsPopup} variant="text" fullWidth>
                        {t("checkoutPage.addressModal.cancel")}
                      </Button>
                      {isCheckout ? (
                        <Button onClick={handleConfirmClick} fullWidth>
                          {t("partnerFinder.search.confirm")}
                        </Button>
                      ) : (
                        <Button startIcon={<ShoppingCartIcon />} href={CART_PAGE} fullWidth>
                          {t("addToCartPopup.checkoutBtn")}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Stack direction="row" gap={1} className="w-100">
                      <Button onClick={closeDetailsPopup} variant="text" fullWidth>
                        {t("bulkUpload.buttons.cancel")}
                      </Button>
                      <Button onClick={handleConfirmClick} fullWidth>
                        {t("partnerFinder.search.confirm")}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </SpModalFilterFooter>
            )
          }
        >
          {!isTablet ? (
            <>
              <SPCompanyDetails
                company={selectedMarker}
                activeTab={activeTab}
                openRegisterSnackbar={openRegisterSnackbar}
                searchValues={searchValues}
                action={
                  <div className="scha__sp__mobile-details__show-map">
                    <Button color="secondary" startIcon={<LocationOnIcon  />} onClick={showMapCb}>
                      {t("page.salespartner.showMap")}
                    </Button>
                  </div>
                }
              />

              <div>
                {!loggedIn && <GmapsCardRightUnregistered company={selectedMarker} searchValues={searchValues} />}
                {registeredUser && !isCheckout && (
                  <GmapsCardRightRegistered
                    shareLinearConfigurationData={shareLinearConfigurationData}
                    companyDetails={selectedMarker.address}
                    close={closeDetailsPopup}
                    company={selectedMarker}
                    searchValues={searchValues}
                  />
                )}
              </div>
            </>
          ) : (
            <SPCompanyDetailsCard
              selectedMarker={selectedMarker}
              activeTab={activeTab}
              shareLinearConfigurationData={shareLinearConfigurationData}
              openRegisterSnackbar={openRegisterSnackbar}
              isCheckout={isCheckout}
              className="m-0"
              searchValues={searchValues}
              action={
                isDesktop ? undefined : (
                  <div className="scha__sp__mobile-details__show-map">
                    <Button color="secondary" startIcon={<LocationOnIcon  />} onClick={showMapCb}>
                      {t("page.salespartner.showMap")}
                    </Button>
                  </div>
                )
              }
            />
          )}
        </Modal>
      )}
    </SalesPartnerStylesRoot>
  );
};

export function createSalesPartnerPage(data?: ConfigurationData) {
  return (
    <GoogleApiProvider>
      <NuqsAdapter>
        <SalesPartnerPage data={data} />
      </NuqsAdapter>
    </GoogleApiProvider>
  );
}

interface LayoutProps {
  results: ReactNode;
  map: ReactNode;
}

interface MobileLayoutProps extends LayoutProps {
  activeTab: string;
  setActiveTab: (a: string) => void;
}

const MobileLayout = ({ results, map, setActiveTab, activeTab }: MobileLayoutProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={"scha__sp__tab-container"}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          fullWidthIndicator={false}
          variant={"fullWidth"}
        >
          <Tab
            value={"results"}
            label={
              <span className={"d-flex flex-direction-column flex-align-center"}>
                <DescriptionIcon />
                {t("page.salespartner.view.list")}
              </span>
            }
          />
          <Tab
            value={"map"}
            label={
              <span className={"d-flex flex-direction-column flex-align-center"}>
                <FmdGoodOutlinedIcon />
                {t("page.salespartner.view.map")}
              </span>
            }
          />
        </Tabs>
      </div>
      <TabContent>{activeTab === "map" ? map : results}</TabContent>
    </>
  );
};

const DesktopLayout = ({ results, map }: LayoutProps) => {
  return (
    <>
      <Row>
        <Col size={4}>{results}</Col>
        <Col size={8}>{map}</Col>
      </Row>
    </>
  );
};

export const TabContent = styled("div")({
  paddingTop: "12px",
});

const SPButtonsRow = styled(Box)(({ theme }) => ({
  gap: theme.spacing(1),
  "&.scha__sp__reverse": {
    flexDirection: "row-reverse",
  },
}));

const SpModalFilterFooter = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)} !important`,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: "0px -1px 5px -2px rgba(0, 0, 0, 0.15), 0px 2px 1px -1px rgba(0, 0, 0, 0.2)",
}));

const SalesPartnerStylesRoot = styled("div")(({ theme }) => ({
  "& .scha__sp__subtitle": {
    marginTop: 0,
    marginBottom: theme.spacing(3),
    ...theme.typography.titleMedium,
    [theme.breakpoints.up("desktop-s")]: {
      ...theme.typography.titleLarge,
      marginBottom: theme.spacing(3),
    },
  },
  "& .scha__search-card": {
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("desktop-s")]: {
      flexDirection: "row",
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
  },
  "& .scha__sp__search-box": {
    marginTop: theme.spacing(-2),
    "& > *": {
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.up("desktop-s")]: {
      flexWrap: "wrap",
      marginTop: 0,
      "& > *": {
        marginTop: 0,
      },
    },
  },
  "& .scha__sp__search-box__radio": {
    alignSelf: "flex-start",
    position: "relative",
    top: 18,
    "&&": {
      flex: "none",
      margin: theme.spacing(-1.125),
      marginRight: theme.spacing(0.375),
      [theme.breakpoints.up("desktop-s")]: {
        marginRight: theme.spacing(1),
      },
    },
  },
  "& .scha__sp__search-box__inputs": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.up("desktop-s")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      "& > * + *": {
        marginTop: 0,
      },
    },
  },
  "& .scha__sp__search-box__or": {
    display: "none",
    [theme.breakpoints.up("desktop-s")]: {
      display: "flex",
      margin: `${theme.spacing(2.25)} ${theme.spacing(4)}`,
      flex: "none",
    },
  },
  "& .scha__sp__search-box__by-name": {
    display: "flex",
    alignItems: "flex-start",
    [theme.breakpoints.up("desktop-s")]: {
      width: "31%",
      flexGrow: 1,
    },
  },
  "& .scha__sp__search-box__by-zip": {
    display: "flex",
    alignItems: "flex-start",
    [theme.breakpoints.down("desktop-s")]: {
      order: -1,
    },
    [theme.breakpoints.up("desktop-s")]: {
      width: "62%",
      flexGrow: 1,
    },
  },
  "& .scha__sp__search-box__submit": {
    [theme.breakpoints.up("desktop-s")]: {
      marginTop: theme.spacing(1),
      flex: "1 0 100%",
    },
  },
  "& .scha__sp__search-box__input--zip-code": {
    [theme.breakpoints.up("desktop-s")]: {
      width: "290px !important",
      flexGrow: 1,
    },
  },
  "& .scha__sp__search-box__input--filter-country": {
    [theme.breakpoints.up("desktop-s")]: {
      width: 140,
      flexGrow: 1,
      marginLeft: theme.spacing(4),
    },
  },
  "& .scha__sp__search-box__input--radius": {
    [theme.breakpoints.up("desktop-s")]: {
      width: 153,
      flexGrow: 1,
    },
  },
  "& .scha__sp__search-box__input-button": {
    top: "24px !important",
  },
  "& .scha__sp-search-input__suggest-list": {
    position: "absolute",
    top: "calc(100% - 16px)",
    left: 0,
    right: 0,
    background: theme.palette.common.white,
    zIndex: 3,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    maxHeight: 226,
    overflow: "auto",
  },
  "& .scha__sp-search-input__suggest-list__entry": {
    color: theme.palette.text.primary,
    border: "none",
    fontWeight: "inherit",
    textAlign: "inherit",
    background: theme.palette.common.white,
    width: "100%",
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    position: "relative",
    "&:hover": {
      zIndex: 1,
      background: theme.palette.surface.main,
    },
  },
  "& .scha__sp__tab-container": {
    position: "sticky",
    top: 0,
    zIndex: 2,
    "& > *": {
      background: theme.palette.common.white,
    },
  },
  "& .scha__sales-partner__search-card__inputs-row": {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    width: "100%",
    paddingBottom: theme.spacing(1.5),
  },
  "& .scha__sales-partner__search-card__inputs-row__checkbox": {
    flex: "none",
    marginLeft: theme.spacing(4),
    position: "relative",
    bottom: 13,
  },
  "& .scha__sales-partner__gmaps-sp-card__right__header": {
    width: "100%",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1.5),
    [theme.breakpoints.up("desktop-s")]: {
      paddingBottom: theme.spacing(2),
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__header__img-container": {
    height: 22,
    width: 149,
    "& img": {
      width: "100%",
      height: "100%",
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__register": {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  "& .scha__sales-partner__gmaps-sp-card__right__register__img-container": {
    height: 194,
    marginRight: "auto",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__register__instruction": {
    width: "100%",
  },
  "& .scha__sales-partner__gmaps-sp-card__right__register__instruction__title": {
    ...theme.typography.titleLarge,
    margin: `${theme.spacing(2)} 0`,
  },
  "& .scha__sales-partner__gmaps-sp-card__right__register__instruction__list": {
    marginBottom: theme.spacing(3),
    "& li": {
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__send-wl__title": {
    ...theme.typography.titleMedium,
    marginTop: 0,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("desktop-s")]: {
      ...theme.typography.titleLarge,
      marginBottom: theme.spacing(3),
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__send-wl__wishlist-input, & .scha__sales-partner__gmaps-sp-card__right__send-wl__comment, & .scha__sales-partner__gmaps-sp-card__right__send-wl__checkbox": {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("desktop-s")]: {
      marginBottom: theme.spacing(2),
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__send-wl__checkbox": {
    display: "flex",
  },
  "& .scha__sales-partner__gmaps-sp-card__right__send-wl__actions": {
    display: "flex",
    flexDirection: "column",
    "& > * + *": {
      marginTop: `${theme.spacing(2)} !important`,
    },
    [theme.breakpoints.up("desktop-s")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      "& > * + *": {
        marginTop: 0,
      },
    },
  },
  "& .scha__sales-partner__gmaps-sp-card__right__send-wl__cancel": {
    alignSelf: "center",
  },
  "& .scha__sp__company-details": {
    display: "flex",
  },
  "& .scha__sp__company-details__content": {
    flex: 1,
  },
  "& .scha__sp__company-details__content-header": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  "& .scha__sp__company-details__content-address": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginTop: theme.spacing(1),
    gap: theme.spacing(4),
    [theme.breakpoints.down("desktop-s")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
    },
  },
  "& .scha__sp__company-details__content-address__col": {
    flex: 1,
    [theme.breakpoints.down("desktop-s")]: {
      alignSelf: "flex-start",
      gap: theme.spacing(1),
    },
  },
  "& .scha__sp__company-details__company-logo": {
    width: 123,
    height: 40,
    objectFit: "contain",
    marginBottom: theme.spacing(1),
  },
  "& .scha__sp__company-details__content__competence-desc": {
    wordBreak: "break-all",
  },
  "& .scha__sp__list-card__name": {
    ...theme.typography.titleMedium,
    marginTop: 0,
    marginBottom: theme.spacing(1),
  },
  "& .scha__sp__list-card__address": {
    marginTop: 0,
    marginBottom: theme.spacing(1),
  },
  "& .scha__sp__list-card__competences": {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  "& .scha__sp__list": {
    listStyle: "none",
    paddingLeft: 0,
    marginBottom: theme.spacing(1.5),
    "& > * + *": {
      marginTop: theme.spacing(1.5),
    },
    [theme.breakpoints.up("desktop-s")]: {
      marginBottom: theme.spacing(3),
      "& > * + *": {
        marginTop: theme.spacing(2),
      },
    },
  },
  "& .scha__sp__list-card": {
    display: "flex",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  "& .scha__sp__list-card__data": {
    flex: 1,
  },
  "& .scha__sp__list-card__company-logo": {
    height: 48,
    width: 147,
    objectFit: "contain",
    marginBottom: theme.spacing(3),
  },
  "& .scha__sp__list-card__logos": {
    flex: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 25,
    "& .scha__sp__list-card__check": {
      padding: 0,
    },
    "& > img": {
      width: 25,
    },
    "& > *:first-child": {
      marginBottom: "auto",
    },
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.up("desktop-s")]: {
      width: 24,
      "& > img": {
        width: 24,
      },
    },
  },
  "& .scha__sp__list-card__distance, & .scha__sp__list-card__preferred-sales-partner": {
    color: theme.palette.text.secondary,
    margin: 0,
  },
  "& .scha__sp__list-card__preferred-sales-partner": {
    marginBottom: theme.spacing(0.625),
  },
  "& .scha__sp__list-card__default-toggle": {
    marginTop: theme.spacing(2),
    display: "flex",
    "& > *:first-child": {
      marginRight: theme.spacing(1),
    },
  },
  "& .scha__sp__mobile-selected-card": {
    position: "absolute",
    bottom: 88,
    left: 16,
    right: 16,
    background: theme.palette.common.white,
    "& .scha__sp__list-card__logos > *:first-child": {
      margin: `${theme.spacing(-1.5)} ${theme.spacing(-1.5)} auto`,
    },
  },
  "& .shared-linear-configs": {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("desktop-s")]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

const SalesPartnerMapPlaceholder = styled("div")(({ theme }) => ({
  minHeight: 520,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
}));
